/**
 * 장비 창고.
 *
 * 창고에는 **착용하지 않은 것만** 둔다. 차는 순간 창고에서 빠지고 벗으면 돌아온다.
 * 보유분 전체를 창고에 두고 착장이 그걸 가리키게 하면, 같은 환두대도를 두 자루
 * 가졌을 때 어느 쪽이 착용분인지 말할 수 없다. 장수와 동료가 같은 목록을 쓰기로
 * 한 이상 중복 보유는 흔한 일이고, 전멸 소실은 착용분만 대상이라 그 구분이
 * 반드시 필요하다.
 *
 * 착용자는 문자열 하나로 가리킨다. 장수는 HERO, 동료는 자기 식별자다.
 */
import { Culture, companionsOf, isRanged } from '../sim/troops.ts';
import {
  ENHANCE_MAX,
  EQUIP_BY_ID,
  SLOT_ORDER,
  Slot,
  commandValue,
  enhanceCost,
  enhanceMaterials,
  equipGainPct,
  equipOf,
  itemKey,
  shopPrice,
  shopStockAt,
} from '../sim/equipment.ts';
import type { EquipDef, EquipItem, Loadout } from '../sim/equipment.ts';
import type { Army } from './army.ts';

/** 장수를 가리키는 착용자 키. 동료 식별자와 겹치지 않는다. */
export const HERO = 'hero';

/** 되팔 때 돌려받는 비율. 사고파는 것으로 돈을 불릴 수 없어야 한다. */
export const SELL_PCT = 50;

/**
 * 되팔 값. 강화한 물건은 **녹여 넣은 같은 물건 몫**(제 몸 하나 + 단계마다 든 재료 — `enhanceMaterials`)에 **강화에 들인
 * 냥의 `SELL_PCT`%**를 더한다.
 *
 * 처음에는 들인 냥을 하나도 돌려주지 않았다 — 강화한 것을 팔면 같은 물건들을 그냥 판 값과 같았다. 플레이한 사람이 청했다:
 * "강화된 무기는 좀 더 비싸게 팔 수 있게 해줘." 들인 냥의 절반을 얹는다. 그래도 강화로 돈을 불릴 수는 없다 — 돌려받는 것은
 * 들인 냥의 절반뿐이다(`tests/enhance.test.ts`). 재료가 단계마다 두 배가 된 뒤로(★3은 물건 여덟) 물건 몫도 그 수를 따른다 —
 * 단계 수로 세면(1 + ★) 녹인 재료의 값이 팔 때 사라진다. 3티어 ★3: 물건 여덟(2,800냥) + 들인 2,450냥의 절반 = 4,025냥.
 */
export function sellPrice(item: EquipDef): number {
  const star = (item as Partial<EquipItem>).star ?? 0;
  let spent = 0;
  let melted = 1;
  for (let n = 1; n <= star; n++) {
    spent += enhanceCost(item, n);
    melted += enhanceMaterials(n);
  }
  return Math.floor((item.cost * SELL_PCT) / 100) * melted + Math.floor((spent * SELL_PCT) / 100);
}

/** 이 군대가 들 수 있는 물건인가. 문화 전용은 남이 못 든다. */
export function canWear(culture: Culture, item: EquipDef): boolean {
  return item.culture === null || item.culture === culture;
}

export function loadoutOf(army: Army, who: string): Loadout {
  return army.loadouts[who] ?? {};
}

/** 창고에 있는 물건을 같은 것끼리 묶는다. 화면이 이 형태로 읽는다. */
export interface StashGroup {
  item: EquipItem;
  count: number;
}

export function stashGroups(army: Army): StashGroup[] {
  const counts = new Map<string, number>();
  for (const id of army.stash) counts.set(id, (counts.get(id) ?? 0) + 1);
  const out: StashGroup[] = [];
  // EQUIPMENT 순서를 따라 모은다. Map 순회 순서에 화면이 기대지 않게 한다. 같은 물건은 높은 강화 단계부터(열쇠마다 한 묶음).
  for (const base of EQUIP_BY_ID.values()) {
    for (let star = ENHANCE_MAX; star >= 0; star--) {
      const key = itemKey(base.id, star);
      const n = counts.get(key);
      const item = n ? equipOf(key) : undefined;
      if (n && item) out.push({ item, count: n });
    }
  }
  // 좋은 것(높은 티어)부터. 플레이한 사람이 청했다: "창고 아이템, 퀄리티 순으로 내림차순 정렬해줘." 같은 티어에서는
  // 더 강화한 것(★)이 앞이다 — 전리품과 창고를 한 칸으로 합치며 다시 청했다("순서는 좋은 아이템 순으로"). 그다음은
  // EQUIPMENT 순서(칸 → 이름) 그대로 — 정렬은 안정적이다.
  out.sort((a, b) => b.item.tier - a.item.tier || b.item.star - a.item.star);
  return out;
}

/**
 * **모두에게 가장 좋은 것으로.** 적은 차례(장수 먼저, 그다음 동료들)로 한 사람씩 `autoEquip`을 한다. 플레이한 사람이
 * 청했다: "장비 페이지 누구에게 섹션에도 '가장 좋은 것으로' 버튼 놔줘. 모든 장수들에게 적용되는 버튼이야."
 *
 * **한 바퀴면 된다.** 모두가 같은 것을 입을 수 있고(`canWear`는 나라만 본다) 점수(`itemScore`)도 사람과 무관하다 — 앞사람이
 * 고를 때 창고의 최고가 A였다면, 뒷사람이 새로 입는 것은 A 이하이고 벗어 놓는 것은 그보다 낮다. 앞사람의 것은 A 이상
 * 이므로 뒷사람이 벗은 것이 앞사람을 낫게 할 수 없다. 처음에는 아무도 안 바꿀 때까지 되풀이했는데 둘째 바퀴는 늘
 * 빈 바퀴였다(리뷰가 300개 조합으로 확인했다). 사람마다 입을 수 있는 것이나 점수가 달라지면 다시 생각한다.
 *
 * 앞선 사람이 좋은 것을 먼저 가진다. 이미 입고 있는 것을 사람끼리 바꿔 주지는 않는다 — 창고(아무도 입지 않은 것)에서만
 * 고른다. 사람마다 바뀐 칸을 돌려준다(바뀐 사람만, 적은 차례로).
 */
export function autoEquipAll(army: Army, whos: readonly string[]): { who: string; slots: Slot[] }[] {
  const out: { who: string; slots: Slot[] }[] = [];
  for (const who of whos) {
    const slots = autoEquip(army, who);
    if (slots.length > 0) out.push({ who, slots });
  }
  return out;
}

export function stashCount(army: Army, id: string): number {
  let n = 0;
  for (const x of army.stash) if (x === id) n++;
  return n;
}

/** 창고에서 한 개를 꺼낸다. 없으면 false. */
function takeOne(army: Army, id: string): boolean {
  const i = army.stash.indexOf(id);
  if (i < 0) return false;
  army.stash.splice(i, 1);
  return true;
}

export function addToStash(army: Army, id: string): void {
  if (!equipOf(id)) return;
  army.stash.push(id);
}

// ---------------------------------------------------------------------------
// 상점
// ---------------------------------------------------------------------------

/**
 * 살 수 있는 물건. 문화 전용은 남의 것을 빼고 보여준다. 가 본 가장 깊은 곳에 따라 전리품 티어도 판다(`shopStockAt` —
 * 얻을 수 있는 티어보다 하나 아래까지).
 */
export function shopFor(army: Army): EquipDef[] {
  return shopStockAt(army.deepest).filter((e) => canWear(army.culture, e));
}

export function buy(army: Army, id: string): boolean {
  const item = EQUIP_BY_ID.get(id);
  if (!item) return false;
  // 상점에 있는 것만 산다: 상점 물건과, 가 본 깊이가 연 전리품 티어(`shopTier` — 얻을 수 있는 티어보다 하나 아래까지).
  // 가장 좋은 티어는 돈으로 살 수 없다 — 깊이 들어갈 까닭이다. 처음에는 3티어부터 모두 막았다.
  if (!shopFor(army).some((e) => e.id === item.id)) return false;
  const price = shopPrice(item);
  if (army.gold < price) return false;
  army.gold -= price;
  army.stash.push(id);
  return true;
}

/**
 * 창고의 물건을 판다. 착용 중인 것은 팔 수 없다. 먼저 벗어야 한다.
 * 반값인 이유는 사고파는 것 자체가 이득이 되면 안 되기 때문이다.
 */
export function sell(army: Army, id: string): boolean {
  const item = equipOf(id);
  if (!item) return false;
  if (!takeOne(army, id)) return false;
  army.gold += sellPrice(item);
  return true;
}

// ---------------------------------------------------------------------------
// 착용
// ---------------------------------------------------------------------------

/**
 * 창고의 물건을 착용시킨다. 그 칸에 있던 것은 창고로 돌아간다.
 * 칸은 물건이 정한다. 부르는 쪽이 칸을 지정하면 투구를 무기 칸에 끼울 수 있다.
 */
export function equipItem(army: Army, who: string, id: string): boolean {
  const item = equipOf(id);
  if (!item) return false;
  if (!canWear(army.culture, item)) return false;
  if (!takeOne(army, id)) return false;

  const worn = { ...loadoutOf(army, who) };
  const prev = worn[item.slot];
  if (prev) army.stash.push(prev);
  worn[item.slot] = id;
  army.loadouts[who] = worn;
  return true;
}

/**
 * 물건의 좋음(자동 착용이 견준다). 티어가 먼저다 — 티어가 곧 설계한 등급이고(1 쇠·가죽 → 5 보물), 전투력 지표는
 * 군기·방패의 값을 다 재지 못한다(`metricBlind`). 같은 티어면 몸에 주는 몫(`equipGainPct`)에 분대에 거는 몫을 더한다.
 */
export function itemScore(item: EquipDef): number {
  const c = commandValue(item);
  const command = c.atkPct - 100 + c.defBonus * 5 + Math.floor(c.gritBonus / 5);
  // 방패의 막는 수는 전투력 지표가 재지 못한다(`metricBlind`) — 따로 더한다. 처음에는 빠져 있어 ★3 나무 방패(11발)와
  // 0단계(8발)가 같은 점수라 "가장 좋은 것으로"가 강화한 방패를 창고에 두었다(리뷰가 짚었다). 같은 바탕이면 단계가
  // 높은 쪽이 낫다(마지막 동점 가르기).
  const star = (item as Partial<EquipItem>).star ?? 0;
  return item.tier * 1000 + equipGainPct(item) + command + (item.blocks ?? 0) + star;
}

/**
 * **가장 좋은 것으로 입힌다.** 칸마다 창고(아무도 입지 않은 것만 있다)에서 이 사람이 들 수 있는 가장 좋은 물건을
 * 골라, 지금 찬 것보다 나으면 바꿔 입힌다. 벗은 것은 창고로 돌아간다. 다른 사람이 입은 것은 건드리지 않는다.
 * 바꾼 칸을 돌려준다. 플레이한 사람이 청했다(Todo8): "장비 페이지에서 자동으로 현재 선택된 장수에게 가장 좋은 장비
 * (다른 사람이 미사용중인)를 착용시키는 기능을 만들어줘."
 */
/** 쏘는 무기 아이템인가(없으면 아니다). */
function bowItem(item: EquipDef | undefined): boolean {
  return item?.weapon !== undefined && isRanged(item.weapon);
}

export function autoEquip(army: Army, who: string): Slot[] {
  const changed: Slot[] = [];
  for (const slot of SLOT_ORDER) {
    const worn = loadoutOf(army, who)[slot];
    const wornItem = worn ? equipOf(worn) : undefined;
    let best: EquipDef | undefined;
    // 창고를 화면과 같은 순서(`stashGroups`: 티어 → 강화 단계 → EQUIPMENT 순서)로 훑는다 — 같은 점수면 앞선 물건이다
    // (순회 순서가 결과를 바꾸지 않게). 그래서 점수가 같은 둘은 더 강화한 쪽을 입는다: 창고가 강화 단계로도 서게 되며
    // 이 동점의 답이 바뀌었다(환두대도 ★1과 철모 ★2는 점수가 같다 — 예전에는 등록이 앞선 환두대도였다). 리뷰가 짚어
    // 여기 적는다: 창고 맨 위에 보이는 것을 입는다는 뜻에서 화면과 같은 순서가 맞다.
    for (const g of stashGroups(army)) {
      if (g.item.slot !== slot || !canWear(army.culture, g.item)) continue;
      // 칼과 활은 서로 바꿔 주지 않는다 — 싸우는 방식이 통째로 바뀐다(휩쓸기 ↔ 멀리서 쏘기, 말 위에서는 활을 못 쓴다).
      // 점수는 티어가 앞서므로 가르지 않으면 좋은 활을 주운 순간 "가장 좋은 것으로"가 장수를 궁수로 만든다. 든 것과
      // 같은 쪽 안에서만 고르고, 맨손이면 칼 쪽이다.
      if (slot === Slot.Weapon && bowItem(g.item) !== bowItem(wornItem)) continue;
      if (!best || itemScore(g.item) > itemScore(best)) best = g.item;
    }
    if (!best || (wornItem && itemScore(wornItem) >= itemScore(best))) continue;
    if (equipItem(army, who, best.id)) changed.push(slot);
  }
  return changed;
}

// ---------------------------------------------------------------------------
// 강화(Todo10)
// ---------------------------------------------------------------------------

/** 강화할 수 있는가와 그 값. 못 하면 까닭(화면이 단추 옆에 적는다). */
export interface EnhanceCheck {
  readonly ok: boolean;
  /** 다음 단계. 끝이면 `ENHANCE_MAX`보다 크다. */
  readonly next: number;
  readonly cost: number;
  readonly reason?: string;
  /** 못 하는 까닭의 갈래: 재료(같은 물건 0단계)가 없다 · 냥이 모자란다. 화면이 까닭마다 달리 적는다. */
  readonly lack?: 'material' | 'gold';
  /** 재료의 이름(0단계 바탕 이름). */
  readonly material?: string;
  /** 이 단계에 드는 재료의 수(`enhanceMaterials`). */
  readonly need?: number;
  /** 모자란 재료의 수(재료가 모자랄 때만 0보다 크다). */
  readonly short?: number;
}

/**
 * 물건을 한 단계 올릴 수 있는가. 재료는 **창고의 같은 물건 0단계**이고 단계마다 두 배로 든다(`enhanceMaterials` — ★1에
 * 1개, ★2에 2개, ★3에 4개). 강화한 것을 재료로 녹이면 들인 냥이 사라져 아무도 하지 않는다. 강화할 물건이 창고의 0단계
 * 그 물건이면 그것 말고 더 있어야 한다(`fromStash`).
 */
export function enhanceCheck(army: Army, key: string, fromStash: boolean): EnhanceCheck {
  const item = equipOf(key);
  if (!item) return { ok: false, next: 0, cost: 0, reason: '모르는 물건' };
  const next = item.star + 1;
  if (next > ENHANCE_MAX) return { ok: false, next, cost: 0, reason: `★${ENHANCE_MAX}가 끝이다` };
  const cost = enhanceCost(item, next);
  const spare = stashCount(army, item.baseId) - (fromStash && item.star === 0 ? 1 : 0);
  // 재료는 0단계라 바탕 이름으로 적는다 — 처음에는 강화한 이름("조우관 ★2")을 적어 ★2를 하나 더 모아야 하는 것처럼 읽혔다.
  const material = EQUIP_BY_ID.get(item.baseId)?.name ?? item.name;
  const need = enhanceMaterials(next);
  if (spare < need) {
    const short = need - spare;
    return { ok: false, next, cost, reason: `재료: ${material} ${short}개 더`, lack: 'material', material, need, short };
  }
  if (army.gold < cost) return { ok: false, next, cost, reason: `${cost.toLocaleString()}냥이 든다`, lack: 'gold', material, need, short: 0 };
  return { ok: true, next, cost, material, need, short: 0 };
}

/**
 * 한 단계 올린다. `who`가 있으면 그 사람이 입은 물건을, 없으면 창고의 물건을. 재료(같은 물건, 단계마다 두 배)와 냥을 쓰고 실패는 없다 —
 * 이 게임의 긴장은 '언제 빠질 것인가'에서 나와야 해서 강화 도박을 겹치지 않는다(Todo10 결정).
 */
export function enhance(army: Army, key: string, who?: string): boolean {
  const check = enhanceCheck(army, key, who === undefined);
  const item = equipOf(key);
  if (!check.ok || !item) return false;
  const worn = who !== undefined ? army.loadouts[who] : undefined;
  if (who !== undefined && worn?.[item.slot] !== key) return false;
  if (who === undefined && !takeOne(army, key)) return false;
  const need = check.need ?? 1;
  for (let taken = 0; taken < need; taken++) {
    if (takeOne(army, item.baseId)) continue;
    // 재료 검사를 지났으니 오지 않는 길이다. 꺼낸 것을 되돌려 계정을 그대로 둔다.
    for (let k = 0; k < taken; k++) army.stash.push(item.baseId);
    if (who === undefined) army.stash.push(key);
    return false;
  }
  army.gold -= check.cost;
  const up = itemKey(item.baseId, check.next);
  if (who !== undefined) army.loadouts[who] = { ...worn, [item.slot]: up };
  else army.stash.push(up);
  return true;
}

/**
 * 장수와 동료가 **입은 것** 가운데 지금 강화할 수 있는 물건이 하나라도 있는가(재료와 냥까지). 출정 화면이 장비 단추에
 * 표를 단다 — 강화는 장비 화면 안에 있어 들어가 보지 않으면 할 수 있는 줄 모른다.
 *
 * 창고의 물건은 세지 않는다. 처음에는 창고의 것도 봤는데, 아무도 쓰지 않는 물건이 전리품으로 둘 쌓이기만 해도 표가
 * 켜졌다 — 플레이한 사람이 청했다: "장수와 동료들이 착용한 장비만 고려해서 표시해줘. 창고나 전리품 섹션에 있는 장비는
 * 제외야." 창고의 것을 강화하는 단추는 장비 화면에 그대로 있다. 재료는 여전히 창고에서 온다(`enhanceCheck`).
 */
export function canEnhanceWorn(army: Army): boolean {
  for (const who of Object.keys(army.loadouts).sort()) {
    if (canEnhanceWornBy(army, who)) return true;
  }
  return false;
}

/**
 * 이 사람(장수 `HERO` 또는 동료 id)이 입은 것 가운데 지금 강화할 수 있는 물건이 있는가. 장비 화면의 "누구에게" 줄이
 * 그 사람에게 표를 단다 — 출정 화면의 표(`canEnhanceWorn`)를 보고 들어왔는데 누구의 것인지 몰라 한 사람씩 눌러 봐야
 * 했다(플레이한 사람이 청했다: "업그레이드 가능한 장비를 가지고 있는 장수 항목에 표식을 달아줘").
 */
export function canEnhanceWornBy(army: Army, who: string): boolean {
  const worn = army.loadouts[who];
  for (const slot of SLOT_ORDER) {
    const key = worn?.[slot];
    if (key && enhanceCheck(army, key, false).ok) return true;
  }
  return false;
}

export function unequip(army: Army, who: string, slot: Slot): boolean {
  const worn = army.loadouts[who];
  const id = worn?.[slot];
  if (!worn || !id) return false;
  const next = { ...worn };
  delete next[slot];
  army.loadouts[who] = next;
  army.stash.push(id);
  return true;
}

/** 전멸로 맞은 칸 하나에 일어난 일: 강화한 물건은 한 단계 깎이고(`to`가 한 단계 아래의 열쇠), 0단계는 잃는다(`to` 없음). */
export interface WornSlot {
  readonly slot: Slot;
  readonly from: string;
  readonly to?: string;
}

/**
 * 전멸로 맞은 칸(`rollWipeLoss`)에 소실을 매긴다. **강화한 물건(★1~3)은 잃지 않고 ★ 하나가 깎인다** — ★0이면 잃는다.
 * 잃은 것은 창고로 돌아가지 않는다. 사라지는 것이다.
 *
 * 처음에는 맞은 칸을 모두 비웠다(`stripSlots`). 강화는 같은 물건과 냥을 녹여 넣은 것이라 ★3을 한 번에 잃으면
 * 물건 여덟(재료가 단계마다 두 배가 되기 전에는 넷)과 그 냥이 사라진다 — 사용자와 정한 기준(실패의 무게, 2026-10-02): "강화한 물건은 통째로 잃지 않고 ★ 하나만
 * 깎인다(★0이면 잃는다)". 맞을지는 예전 주사위 그대로다(난수 수열이 바뀌지 않는다).
 */
export function wearDown(army: Army, who: string, slots: readonly Slot[]): WornSlot[] {
  const worn = army.loadouts[who];
  if (!worn) return [];
  const next = { ...worn };
  const out: WornSlot[] = [];
  for (const slot of slots) {
    const id = next[slot];
    if (!id) continue;
    const item = equipOf(id);
    if (item && item.star > 0) {
      const to = itemKey(item.baseId, item.star - 1);
      next[slot] = to;
      out.push({ slot, from: id, to });
    } else {
      delete next[slot];
      out.push({ slot, from: id });
    }
  }
  army.loadouts[who] = next;
  return out;
}

/**
 * 칸을 비운다. 창고로 돌아가지 않는다. 사라지는 것이다. 잃은 물건의 식별자를 돌려준다. 전멸의 소실은 `wearDown`이 한다 —
 * 강화한 물건은 깎일 뿐이다.
 */
export function stripSlots(army: Army, who: string, slots: readonly Slot[]): string[] {
  const worn = army.loadouts[who];
  if (!worn) return [];
  const next = { ...worn };
  const gone: string[] = [];
  for (const slot of slots) {
    const id = next[slot];
    if (!id) continue;
    gone.push(id);
    delete next[slot];
  }
  army.loadouts[who] = next;
  return gone;
}

/**
 * 저장에서 돌아온 창고를 정리한다.
 *
 * 옛 저장에는 창고 자체가 없고, 목록에서 빠진 물건이 남아 있을 수도 있다.
 * 병종과 달리 장비는 없어졌다고 계정을 통째로 버릴 이유가 없다. 모르는 것만
 * 조용히 털어낸다.
 *
 * 물건이 남아 있는 한 창고로 돌려보낸다. 착장에서 지우기만 하면 그 물건은
 * 창고에도 없고 입힐 수도 팔 수도 잃을 수도 없는 고아가 된다. 동료 식별자가
 * 바뀌었을 때 그 동료의 착장 전체가 그렇게 사라진다.
 */
export function normalizeStash(army: Army): void {
  // 강화한 물건은 열쇠(`w_x+2`)로 남는다. 단계 표기가 틀린 것은 모르는 물건처럼 털어낸다.
  army.stash = Array.isArray(army.stash) ? army.stash.filter((id) => typeof id === 'string' && equipOf(id) !== undefined) : [];

  // 착장을 가질 수 있는 사람. 이 밖의 키는 남은 저장 찌꺼기다.
  const known = new Set<string>([HERO, ...companionsOf(army.culture).map((c) => c.id)]);

  const src = army.loadouts;
  const out: Record<string, Loadout> = {};
  if (src && typeof src === 'object') {
    for (const who of Object.keys(src).sort()) {
      const worn = src[who];
      if (!worn || typeof worn !== 'object') continue;
      const kept: Loadout = {};
      for (const slot of SLOT_ORDER) {
        const id = worn[slot];
        if (!id) continue;
        const item = equipOf(id);
        // 목록에서 빠진 물건은 돌려보낼 곳도 없다. 그냥 사라진다.
        if (!item) continue;
        // 칸이 맞지 않는 저장은 손으로 고친 것이다. 물건은 창고로 돌려보낸다.
        // 문화가 맞지 않는 것도 마찬가지다. 창고의 남의 물건은 팔 수 있게 두므로,
        // 착장 쪽만 태워 없애면 앞뒤가 다르다.
        if (item.slot !== slot || !canWear(army.culture, item) || !known.has(who)) {
          army.stash.push(id);
          continue;
        }
        kept[slot] = id;
      }
      if (known.has(who)) out[who] = kept;
    }
  }
  army.loadouts = out;
}
